import { JoinAdvancedFilterModel, TextAdvancedFilterModel } from 'ag-grid-enterprise';
import { Observable, map, of } from 'rxjs';

import { InputChoices } from '@camelot/form-model';
import { isNonNullable } from '@camelot/utils';

import { AsyncEnumRendererComponent } from '../../../components/templates/async-enum-renderer/async-enum-renderer.component';
import { BaseCol, IInputOptions } from '../base-col';
import { AsyncEnumEditorComponent } from './async-enum-editor.component';
import { AsyncEnumFilterComponent } from './async-enum-filter.component';

type FilterTyping = JoinAdvancedFilterModel | TextAdvancedFilterModel;

export class AsyncEnumCol extends BaseCol<FilterTyping> {
  public readonly hasNumericId = this.data.col.asyncEnumInfo?.numericId ?? false;

  get values() {
    return this._extractFiltersByFilterType(this.filterInstance, ['equals'])
      .filter(cond => cond.filterType === 'text')
      .map((cond: any) => cond.filter?.toString())
      .filter(isNonNullable);
  }

  public getValues$(): Observable<{ id: string; name: string }[]> {
    return this.data.col.asyncEnumInfo?.values$ ?? of([]);
  }

  public override getColDef() {
    return {
      ...super.getColDef(),
      ...{
        cellDataType: 'text',
        cellEditor: AsyncEnumEditorComponent,
        cellRenderer: AsyncEnumRendererComponent,
        filter: AsyncEnumFilterComponent,
        type: 'string',
      },
    };
  }

  public override getFiltersModel(data: any): FilterTyping | null {
    const value = data[this.key];
    if (!value) {
      return null;
    }

    if (!Array.isArray(value)) {
      return {
        colId: this.key,
        filter: value,
        filterType: 'text',
        type: 'equals',
      };
    }
    const filters = value.reduce<TextAdvancedFilterModel[]>((acc, val) => {
      if (val) {
        acc.push({
          colId: this.key,
          filter: val,
          filterType: 'text',
          type: 'equals',
        });
      }
      return acc;
    }, []);

    if (filters.length === 0) {
      return null;
    }
    if (filters.length === 1) {
      return filters[0];
    }
    return {
      conditions: filters,
      filterType: 'join',
      type: 'OR',
    };
  }

  public override getInputForm(options?: IInputOptions) {
    return new InputChoices({
      key: this.key,
      label: options?.fromView ? '' : this.inputLabel,
      multiple: options?.fromView ? true : false,
      onlyTemplate: options?.fromView,
      options: this.getValues$().pipe(map(values => values.map(v => ({ ...v, data: v.id })))),
      value: this.values,
      withSearch: true,
    });
  }
}
